import React, { useContext, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import { ReloadIcon } from '@radix-ui/react-icons';
import {
  Flex,
  IconButton,
  Text,
  Tooltip,
  getAvatarColor
} from '@raystack/apsara';

import {
  ActionStep,
  ImageSearchParameters,
  InsightsHandlerResponse,
  RatingType,
  StepWithID
} from '@src/clients/seeker/api';
import { IndexAgentResponse } from '@src/clients/seeker/api';
import { UserAvatar } from '@src/components';
import { CustomMarkdownRenderers } from '@src/components/CoPilot/CustomRenderMdElements/CustomRenderMd';
import { MarkdownTableRenderer } from '@src/components/CoPilot/CustomTable/MarkdownTableRenderer';
import { ImageSearchParamsUI } from '@src/components/CoPilot/ImagesSearchParams';
import { TypingLoader } from '@src/components/Custom/TypingLoader';
import { useCopilotSelector } from '@src/contexts';
import { IdentityContext } from '@src/contexts/identity/context';
import { Show } from '@src/hoc';
import { useCopilot } from '@src/hooks/useCopilot';
import { getInitials } from '@src/utils';
import {
  AURORA_INTELLIGENCE_THUMBNAIL_16,
  RATING_TYPES,
  ROLE,
  TOOL_TYPES
} from '@src/utils/constants/COPILOT_CONSTANTS';

import { AiIndiceList } from './AiIndiceList';
import { AiInsightCard } from './AiInsightCard';
import CitationGroup from './CitationGroup';
import FeedbackSection from './FeedbackSection';

import styles from './Chatlist.module.css';

interface StepItemProps {
  item: StepWithID;
  index: number;
  steps: StepWithID[];
  key: string | number;
}

interface ChatItemProps {
  id: string;
  rating?: string;
  isUser: boolean;
  content: React.ReactNode;
  messageLoading?: boolean;
  userMessage?: StepWithID;
}

const Feedback = ({
  id,
  rating
}: {
  id: string;
  rating: string;
  showFeedback: boolean;
  messageLoading: boolean;
}) => {
  const [ratingStatus, setRatingStatus] = useState<RatingType>(
    (rating as RatingType) || RATING_TYPES.NEUTRAL
  );
  const chatId = useCopilotSelector(state => state.chatId);
  const { rateAssistantMessage, mutateMessages } = useCopilot();

  const handleFeedbackClick = async (rating: RatingType) => {
    try {
      setRatingStatus(rating);
      await rateAssistantMessage(chatId, id, rating);
      mutateMessages(chatId);
    } catch (error) {
      console.error('Error giving rating:', error);
      setRatingStatus(RATING_TYPES.NEUTRAL);
    }
  };

  return (
    <FeedbackSection
      ratingStatus={ratingStatus}
      handleFeedbackClick={handleFeedbackClick}
    />
  );
};

export const ChatItem = ({
  id,
  rating = '',
  content,
  isUser,
  userMessage,
  messageLoading = false
}: ChatItemProps) => {
  const [showFeedback, setShowFeedback] = useState(false);

  const isPolling = useCopilotSelector(state => state.isPolling);
  const resetMessageId = useCopilotSelector(state => state.resetMessageId);

  const { retryAssistantMsgGeneration } = useCopilot();

  const { user } = useContext(IdentityContext);
  const userInitials = getInitials(user!);
  const userImage = user?.avatar || '';

  return (
    <Show
      when={!(messageLoading && isPolling)}
      fallback={
        <Flex align='center' gap={3} justify='start'>
          <UserAvatar
            size={1}
            radius='small'
            src={AURORA_INTELLIGENCE_THUMBNAIL_16}
            fallback={'AI'}
            className={styles.avatar}
          />
          <TypingLoader />
        </Flex>
      }
    >
      <Flex
        key={id}
        gap={3}
        align='start'
        direction='row'
        onMouseEnter={() => {
          setShowFeedback(!isUser ? true : false);
        }}
        onMouseLeave={() => {
          setShowFeedback(false);
        }}
        style={{
          cursor: 'pointer',
          position: 'relative',
          paddingBottom: isUser ? 0 : 16
        }}
      >
        <UserAvatar
          size={1}
          radius='small'
          src={isUser ? userImage : AURORA_INTELLIGENCE_THUMBNAIL_16}
          fallback={isUser ? userInitials : 'AI'}
          color={getAvatarColor(isUser ? user?.id || '' : 'AI')}
          className={styles.avatar}
        />
        <Flex direction='column' gap={5} className={styles.contentWrapper}>
          <Show
            when={isUser}
            fallback={
              <Show
                when={resetMessageId !== id}
                fallback={
                  <Text size='small'>
                    Something went wrong. Please try again later or retry the
                    request.
                  </Text>
                }
              >
                {content}
              </Show>
            }
          >
            {content}
          </Show>
          <Show when={!isUser && !messageLoading && showFeedback}>
            <Flex
              gap={3}
              align='center'
              justify='start'
              className={styles.msgActionsWrapper}
            >
              <Show when={resetMessageId === id}>
                <IconButton
                  size={3}
                  data-test-id='retry-chat'
                  onClick={() => retryAssistantMsgGeneration(userMessage!, id)}
                >
                  <Tooltip message='Retry message' side='bottom'>
                    <ReloadIcon color='var(--rs-color-foreground-base-secondary)' />
                  </Tooltip>
                </IconButton>
              </Show>
              <Feedback
                id={id}
                rating={rating}
                showFeedback={showFeedback}
                messageLoading={messageLoading}
              />
            </Flex>
          </Show>
        </Flex>
      </Flex>
    </Show>
  );
};

const StepItem: React.FC<StepItemProps> = React.memo(
  ({ key, item, index, steps }) => {
    const isUser = item.step.role === ROLE.USER;

    // Helper function to render content for assistant roles
    const renderAssistantContent = () => {
      const step = item.step as ActionStep;

      switch (step.name) {
        case TOOL_TYPES.IMAGE_SEARCH: {
          const tool_data = step.tool_data as ImageSearchParameters;
          const queryParameters = tool_data?.explore_filters;
          return (
            <Show
              when={!!queryParameters}
              fallback={
                <Text>
                  The area you are trying to search is larger than the allowed
                  threshold
                </Text>
              }
            >
              <Flex direction='column' gap='small'>
                <ImageSearchParamsUI queryParameters={queryParameters} />
                <ReactMarkdown components={CustomMarkdownRenderers}>
                  {item.step.content || ''}
                </ReactMarkdown>
              </Flex>
            </Show>
          );
        }
        case TOOL_TYPES.INFORMATION: {
          return <CitationGroup currentIndex={index} steps={steps} />;
        }
        case TOOL_TYPES.INSIGHTS: {
          return <AiInsightCard step={step} />;
        }
        case TOOL_TYPES.INDEX: {
          return <AiIndiceList step={step} />;
        }
        default:
          return null;
      }
    };

    const renderMarkdownContent = (content: string) => {
      if (content.includes('|')) {
        return <MarkdownTableRenderer content={content} />;
      }
      return (
        <ReactMarkdown components={CustomMarkdownRenderers}>
          {content}
        </ReactMarkdown>
      );
    };

    const step = item.step as ActionStep;
    if (
      step?.name === TOOL_TYPES.INSIGHTS &&
      (step?.tool_data as InsightsHandlerResponse)?.blocks?.length === 0
    ) {
      return null;
    }

    if (
      step?.name === TOOL_TYPES.INDEX &&
      (step?.tool_data as IndexAgentResponse)?.indices?.length === 0
    ) {
      return null;
    }

    return (
      <Flex
        key={key}
        gap='small'
        direction='column'
        className={styles.contentWrapper}
      >
        <Show when={isUser}>
          <ReactMarkdown components={CustomMarkdownRenderers}>
            {item.step.content || ''}
          </ReactMarkdown>
        </Show>
        <Show
          when={
            item.step.role === ROLE.ASSISTANT &&
            !item.step.tool_calls &&
            item.step.content !== null
          }
        >
          {renderMarkdownContent(item.step.content || '')}
        </Show>
        <Show when={item.step.role === ROLE.TOOL && !!item.step.tool_data}>
          {renderAssistantContent()}
        </Show>
      </Flex>
    );
  }
);

StepItem.displayName = 'StepItem';

export default StepItem;
